import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  Layout,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

const faqs = [
  {
    q: "What is iBeacon and how does it work in a mobile app?",
    a: "iBeacon is Apple's Bluetooth Low Energy proximity standard. Beacon hardware broadcasts a small radio signal; our mobile app picks it up, calculates distance, and triggers contextual actions — notifications, navigation prompts, asset tracking updates, or analytics events. We implement the full stack: hardware configuration, iOS and Android SDK integration, and backend event processing.",
  },
  {
    q: "Do you work with all beacon hardware brands?",
    a: "Yes. We work with any BLE-based beacon hardware — Estimote, Kontakt.io, BlueUp, custom OEM beacons, and others. If you already have hardware in deployment or a preferred supplier, we adapt to your stack. If you are still selecting hardware, we can advise on the best fit for your use case.",
  },
  {
    q: "How long does iBeacon app development take?",
    a: "A focused iBeacon application typically takes 2–4 months from kick-off to store release. Timeline depends on app complexity, number of platforms, backend integrations, and UX scope. We break every project into clear stages so you always know where things stand.",
  },
  {
    q: "How do you reduce risk in iBeacon projects?",
    a: "We start with a hardware sample analysis and document communication behaviour before writing a line of app code. This surfaces range issues, firmware quirks, and edge cases early — before they become expensive problems. We also run real-device tests throughout development rather than relying on simulators.",
  },
  {
    q: "Can you ensure GDPR compliance for beacon apps?",
    a: "Yes. GDPR compliance is built into our process. We advise on consent flows, data minimisation, retention policies, and cross-border transfer rules. All user data is processed and stored within the EU under the oversight of our Polish team.",
  },
  {
    q: "Can you integrate iBeacon functionality into an existing app?",
    a: "Yes. We can add beacon support — proximity detection, indoor positioning, asset tracking, or analytics — to an existing iOS or Android app. We start with a code review to understand the current architecture before proposing the integration approach.",
  },
  {
    q: "What does your iBeacon app development service include?",
    a: "It includes requirements workshops, hardware sample analysis, UX/UI design, iOS and Android development, backend integration, store submission, and post-launch support. You can engage us for the full lifecycle or for specific stages depending on where you need help.",
  },
  {
    q: "Do you help with App Store and Google Play submission?",
    a: "Yes. Store submission is a standard part of our service. We prepare all required assets, handle the Apple and Google review processes, and help you navigate any formal or technical requirements specific to Bluetooth and location-aware apps.",
  },
  {
    q: "Can you sign an NDA before discussing the project?",
    a: "Yes. We can sign an NDA before any technical discussions. We can work with our standard template or review your document.",
  },
] as const;

export const title = "iBeacon App Development Services | Blues Brackets";
export const description =
  "European iBeacon app development company. We build iOS and Android applications for beacon-based proximity, indoor navigation, and asset tracking. Smooth process, reduced risk, GDPR compliant.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "iBeacon app development",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "iBeacon app development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Who it is for",
    values: "Value",
    services: "Services",
    process: "Process",
    about: "About us",
    faq: "FAQ",
  },
  cta: "Estimate your project",
};

export const heroCopy: HeroCopy = {
  header: "iBeacon App Development",
  headerTail: "by a European team",
  description:
    "We build iOS and Android applications for iBeacon and Bluetooth beacon deployments. Proximity marketing, indoor navigation, asset tracking — delivered with a smooth process and reduced project risk.",
  cta: {
    text: "Free consultation",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "See our process",
    href: "#proces",
  },
  benefits: [
    { text: "European team, GDPR compliant", Icon: CheckCircle },
    { text: "Smooth, transparent process", Icon: CheckCircle },
    { text: "Risk reduced from day one", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Who we work with",
    h3: "Is this offer for you?",
    p: "We help companies that want to build iBeacon applications or improve existing beacon-based products. Does any of this sound familiar?",
  },
  points: [
    {
      Icon: Cpu,
      title: "You manufacture or distribute beacon hardware",
      description:
        "Your devices are ready but you need a trusted software partner to build the companion app and backend.",
    },
    {
      Icon: Target,
      title: "You want proximity or indoor navigation features",
      description:
        "You need an app that detects nearby beacons and reacts — showing offers, guiding visitors, or logging presence.",
    },
    {
      Icon: AlertCircle,
      title: "Your current iBeacon app is underperforming",
      description:
        "It loses beacons, misfires triggers, or the UX frustrates users. You need a stable, well-designed replacement.",
    },
    {
      Icon: RefreshCw,
      title: "You have beacons deployed but no good app",
      description:
        "The hardware is already on the wall or shelf, but the software has never done it justice. We can change that.",
    },
    {
      Icon: Users,
      title: "You need analytics around physical locations",
      description:
        "You want data on customer presence, dwell time, and movement patterns to support business decisions.",
    },
  ],
  cta: {
    h4: "Sounds like your situation?",
    text: "Let us discuss your beacon project and how we can make it work.",
    href: "#kontakt",
    buttonText: "Book a free consultation",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Why choose us",
    h3: "A reliable iBeacon development company",
    p: "We combine deep Bluetooth expertise, mobile development, and a structured process. Partnering with a European specialist reduces risk and keeps your project on track.",
    imageAlt: "Software development and code",
  },
  values: [
    {
      Icon: Bluetooth,
      title: "iBeacon and BLE is our specialty",
      description:
        "We have hands-on experience with iBeacon, Eddystone, and custom BLE beacon profiles. We know ranging quirks, multipath interference, iOS Core Location behaviour, and Android BLE edge cases that trip up teams without this background.",
    },
    {
      Icon: Shield,
      title: "We reduce project risk from the start",
      description:
        "Before writing app code we analyse the hardware, document the beacon protocol, and surface any firmware or range limitations. Problems found in week one cost far less than problems found at launch.",
    },
    {
      Icon: Layout,
      title: "Smooth, transparent cooperation",
      description:
        "Clear scope, defined stages, and regular updates. You always know what is being built, what is next, and what it will cost. No surprises, no scope creep without agreement.",
    },
    {
      Icon: TrendingUp,
      title: "An app built to grow",
      description:
        "Your iBeacon application can expand into analytics, new product lines, and additional beacon networks. We architect for the long term, not just the first release.",
    },
    {
      Icon: Globe,
      title: "European team, European standards",
      description:
        "We are based in Poland and operate under EU law. GDPR compliance, data residency in the EU, and understanding of European business requirements come built in.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Services",
    h3: "End-to-end iBeacon app development",
    p: "From initial workshops and hardware analysis through to store release and ongoing support. We cover every stage of your iBeacon project.",
  },
  services: [
    {
      Icon: PenTool,
      title: "Requirements workshops",
      description:
        "We turn your beacon use cases — proximity marketing, indoor navigation, asset tracking, attendance — into a clear product brief with defined stages and a realistic estimate.",
    },
    {
      Icon: Search,
      title: "Hardware and protocol analysis",
      description:
        "We test your beacon hardware, document the advertising format and ranging behaviour, and identify any limitations before development begins. This is how we reduce risk.",
    },
    {
      Icon: Layers,
      title: "UX/UI design",
      description:
        "We design interfaces that make beacon interactions feel natural and useful — not intrusive. Mockups are reviewed by you before any code is written.",
    },
    {
      Icon: Smartphone,
      title: "iOS and Android app development",
      description:
        "We build a single cross-platform app that handles beacon scanning, ranging, and event logic on both platforms. Tested on physical devices in realistic environments.",
    },
    {
      Icon: Server,
      title: "Backend and integrations",
      description:
        "Event pipelines, presence analytics, push notifications, CMS integration — we build and connect the backend services your iBeacon application needs.",
    },
    {
      Icon: PlaySquare,
      title: "Store release",
      description:
        "We manage Apple App Store and Google Play submission, including Bluetooth and location permission requirements that are specific to beacon apps.",
    },
    {
      Icon: LifeBuoy,
      title: "Post-launch support",
      description:
        "We monitor the app, handle OS updates that affect BLE behaviour, and continue developing features as your beacon deployment grows.",
    },
  ],
  cta: {
    h4: "Ready to start?",
    p: "Tell us about your beacon project. We will scope it, estimate it, and show you how we plan to deliver it.",
    button: {
      text: "Book a free consultation",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Our process",
    h3: "How your iBeacon app gets built",
    p: "A structured, low-risk path from first conversation to live application. Each step has a clear output so you always know what happens next.",
  },
  stepNumber: "Step",
  steps: [
    {
      Icon: Calendar,
      title: "Free online meeting",
      duration: "within a few hours",
      description:
        "A 30-minute call with a BLE specialist. You get a written summary and an initial project estimate the same day.",
    },
    {
      Icon: FileText,
      title: "Requirements workshops",
      duration: "within a few days",
      description:
        "A 2-hour online workshop produces a PDF report, a detailed estimate, and a stage-by-stage delivery proposal.",
    },
    {
      Icon: Search,
      title: "Hardware sample analysis",
      duration: "1–2 weeks",
      description:
        "We test your beacon hardware and document its behaviour. You get written results and confidence that the hardware can support your project.",
    },
    {
      Icon: PenTool,
      title: "Contract and schedule",
      duration: "schedule agreement",
      description:
        "We agree on start and end dates with a contractual commitment to the timeline.",
    },
    {
      Icon: Code,
      title: "Stage-by-stage delivery",
      duration: "min. 2–3 months",
      description:
        "UX mockups, development, and testing in defined stages. You test demo builds as they are delivered.",
    },
    {
      Icon: ShoppingBag,
      title: "Store release",
      duration: "min. 2 weeks",
      description:
        "We handle Apple and Google review, including Bluetooth and location entitlements specific to iBeacon apps.",
    },
    {
      Icon: CheckCircle,
      title: "Handover and support",
      duration: "ongoing",
      description:
        "You receive the source code and repositories, or we begin a continuous support and maintenance engagement.",
    },
    {
      Icon: Zap,
      title: "Further development",
      duration: "future versions",
      description:
        "Analytics, new beacon regions, additional products — the app is ready to grow with your deployment.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Start your iBeacon project",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "About us",
    h3: "A European team you work with directly",
    p1: "We are a small, senior team based in Poland — specialists in Bluetooth Low Energy, mobile development, and embedded systems.",
    p2: "Michał, Tomasz, and Kuba handle implementation and code stability, while Mateusz designs UX that makes beacon-driven products easy and pleasant to use.",
    p3: "GDPR compliant",
    p4: "Data in the EU",
    Icon: ShieldCheck,
    imageAlt: "Team working on an iBeacon app in the office",
  },
  values: [
    {
      Icon: Users,
      title: "You work directly with us",
      description:
        "No account managers, no outsourcing. You communicate directly with the engineers building your app.",
    },
    {
      Icon: MapPin,
      title: "European standards built in",
      description:
        "We understand EU regulations, GDPR, and what European end users expect from a location-aware app.",
    },
    {
      Icon: ShieldCheck,
      title: "Data security is not an afterthought",
      description:
        "All user data is stored in the EU and handled according to GDPR from the first line of code.",
    },
  ],
  cta: {
    text: "Contact us",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "iBeacon app development — common questions",
  },
  faqs,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Looking for an iBeacon development partner?",
    p: "Book a free consultation, tell us about your beacon deployment, and get an initial estimate. We reply within 24 hours on business days.",
  },
  cta: {
    href: "#kontakt",
    text: "Book a free consultation",
    p: "European iBeacon app development company. We build iOS and Android beacon applications with a smooth process and reduced project risk.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Quick links",
    links: [
      { href: "#dla-kogo", text: "Who we work with" },
      { href: "#uslugi", text: "Services" },
      { href: "#proces", text: "Process" },
      { href: "#faq", text: "FAQ" },
    ],
  },
  knowledgeBase: {
    h4: "Knowledge base",
    links: [
      {
        href: "/blogs/is_bluetooth_le_right_for_your_product",
        text: "Is Bluetooth LE the right technology for your product or MVP?",
      },
      {
        href: "/blogs/bluetooth_le_product_architecture",
        text: "Bluetooth LE IoT product architecture and development",
      },
      {
        href: "/blogs/ble_mobile_application_support",
        text: "BLE support on the mobile application side",
      },
      {
        href: "/blogs/ble_embedded_development_platforms",
        text: "Best platforms for Bluetooth LE product development",
      },
      {
        href: "/blogs/auracast_mcu",
        text: "ESP32 Auracast: Does It Work? Best MCUs for LE Audio",
      },
      {
        href: "/blogs/ble_hardware_2026",
        text: "Best hardware for Bluetooth Low Energy BLE development in 2026",
      },
    ],
  },
  copyright: "bluesBrackets. All rights reserved.",
  privacyPolicy: "Privacy Policy",
  privacyPolicyHref: "/privacy-policy",
  copiedLabel: "Copied to clipboard",
  contactForm: {
    heading: "Contact us",
    fields: {
      name: {
        label: "Full name",
        placeholder: "John Smith",
        requiredError: "Enter your full name",
      },
      email: {
        label: "Email address",
        placeholder: "john@company.com",
        requiredError: "Enter your email address",
        invalidError: "Enter a valid email address",
      },
      topic: {
        label: "Project description",
        placeholder:
          "Briefly describe your iBeacon project and what you need...",
        requiredError: "Describe your project",
        maxLengthError: "The description can be up to 2000 characters long",
      },
    },
    submit: "Send inquiry",
    submitting: "Sending...",
    successMessage: "Message sent!",
    errorMessage: "Could not send the message :(",
    responseTime: "We reply within 24 hours on business days.",
    emailSubjectPrefix: "Full name",
  },
};
